// ЕДИНСТВЕННЫЙ HTTP-клиент приложения. Все запросы к бэкенду — только через apiGet/apiSend/apiRequest.
// Знает: базу API, X-Access-Token (токен ПРИЛОЖЕНИЯ профиля football-telegram, не пользователя), X-Platform, X-Device-Id,
// X-App-Version (номер релиза), язык (?lang=), конверт ошибок {detail, code}. Bearer даёт сессия (setBearerProvider).
import { lang } from '../i18n/i18n';

// dev: относительный адрес → прокси Vite; prod: абсолютная база из окружения сборки.
const API_BASE = import.meta.env.DEV ? '' : (import.meta.env.VITE_API_BASE ?? 'https://api.aplback.com');
const ACCESS_TOKEN: string = import.meta.env.VITE_API_ACCESS_TOKEN ?? '';
/** Номер релиза сборки (scripts/deploy.sh: VITE_RELEASE) — X-App-Version и app_version аналитики; вне выката — dev. */
export const APP_VERSION: string = import.meta.env.VITE_RELEASE || 'dev';

/**
 * Идентификатор установки (X-Device-Id, 8–64 знака): опознание гостя, без него голос в опросе не принимается.
 * localStorage может быть недоступен (приватный режим) — тогда id живёт до перезапуска.
 */
export const DEVICE_ID: string = (() => {
  const key = 'football.device_id';
  try {
    const saved = localStorage.getItem(key);
    if (saved && /^[A-Za-z0-9-]{8,64}$/.test(saved)) return saved;
    const fresh = crypto.randomUUID();
    localStorage.setItem(key, fresh);
    return fresh;
  } catch {
    return crypto.randomUUID();
  }
})();

export type ApiErrorKind = 'network' | 'unreachable' | 'http';

/** Ошибка запроса. Ветвиться только по `code` (detail не локализован) или по `kind`/`status`.
 *  kind: network — устройство без сети (navigator.onLine = false); unreachable — сеть есть, ответа нет (сервер лежит,
 *  ответ без CORS-заголовков — так браузер видит 429/502 от nginx); http — ответ с кодом. */
export class ApiError extends Error {
  constructor(
    readonly kind: ApiErrorKind,
    readonly status: number,
    readonly code: string | null,
    readonly retryAfterSeconds: number | null = null,
    /** Тело ошибки (например, limit у favorite_limit_exceeded). */
    readonly payload: Record<string, unknown> | null = null,
  ) {
    super(`${kind} ${status} ${code ?? ''}`.trim());
  }
}

/** Свои параметры доклеиваем к адресу сервера, его собственные не трогаем. `+` кодируется как %2B. */
export function withParams(path: string, params: Record<string, string | number | undefined>): string {
  const [base, query = ''] = path.split('?', 2) as [string, string?];
  const existing = new URLSearchParams(query);
  const parts = query ? [query] : [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || existing.has(key)) continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return parts.length ? `${base}?${parts.join('&')}` : base;
}

/** Источник Bearer (подключает session.ts; так клиент не зависит от сессии). */
export interface BearerProvider {
  /** Сессия есть (вошёл через Telegram) — запросы идут с Bearer. */
  has(): boolean;
  token(): Promise<string>;
  /** 401 на запросе с этим токеном: сбросить его, следующий token() обновит (single-flight). */
  invalidate(rejected: string): void;
}
let bearer: BearerProvider | null = null;
export function setBearerProvider(provider: BearerProvider | null): void { bearer = provider; }

/** auth: true — только с Bearer (личное: избранное аккаунта); 'optional' — с Bearer, если сессия есть (экраны: вошедшему
 *  сервер отвечает с учётом аккаунта), иначе без; false — без Bearer (вход, обновление). */
export interface RequestOptions { body?: unknown; signal?: AbortSignal; auth?: boolean | 'optional'; headers?: Record<string, string> }

/** Коды 401 «токен человека не принят» (гид v5 §1.3): `access_token_expired` — просрочка access, `invalid_refresh_token` —
 *  отозван/мусор (старые серверы так же слали и просрочку). На оба — обновить сессию и повторить ОДИН раз. Прочие 401
 *  (прежде всего `invalid_access_token` — ключ приложения, не JWT человека) обновлением не лечатся: сразу наверх. */
const SESSION_REJECTED_CODES = new Set(['access_token_expired', 'invalid_refresh_token']);
export function isSessionRejected(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401 && error.code !== null && SESSION_REJECTED_CODES.has(error.code);
}

/** Запрос с Bearer: на 401 с кодом сессии (isSessionRejected) — одно обновление токена и ОДИН повтор. Необязательный
 *  Bearer, который не удалось получить или обновить, — запрос без него: экран не падает из-за сессии. */
export async function apiRequest<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const plain = () => request<T>(method, path, opts.body, opts.signal, opts.headers);
  const optional = opts.auth === 'optional';
  if (!opts.auth || !bearer || (optional && !bearer.has())) return plain();
  const provider = bearer;
  const withToken = (token: string) => request<T>(method, path, opts.body, opts.signal, { ...opts.headers, Authorization: `Bearer ${token}` });
  let first: string;
  try { first = await provider.token(); } catch (e) { if (optional) return plain(); throw e; }
  try {
    return await withToken(first);
  } catch (error) {
    if (!isSessionRejected(error)) throw error;
    provider.invalidate(first);
    let second: string;
    try { second = await provider.token(); } catch (e) { if (optional) return plain(); throw e; }
    return withToken(second);
  }
}

/** Исход пишущего запроса — для аналитики (src/analytics): клиент о ней не знает, только сообщает слушателю. */
export interface ApiAction { method: string; path: string; status: number; code: string | null; reason?: unknown; /** ответ */ body?: unknown; /** что отправили */ sent?: unknown }
let actionListener: ((a: ApiAction) => void) | null = null;
export function setActionListener(fn: ((a: ApiAction) => void) | null): void { actionListener = fn; }
function report(a: ApiAction): void {
  if (!actionListener || a.method === 'GET') return;
  try { actionListener(a); } catch { /* аналитика не роняет запрос */ }
}

/** Предел ожидания ответа: подвисший сервер не держит экран вечно — по истечении ApiError('unreachable'). */
export const REQUEST_TIMEOUT_MS = 15_000;

/** Сигнал «вызывающий отменил ИЛИ истёк таймаут». Таймер свой (setTimeout), а не AbortSignal.timeout: его снимает
 *  done() сразу после ответа (не висит 15 с), и его видят фейковые таймеры тестов. timedOut() отличает таймаут от отмены. */
function withTimeout(caller: AbortSignal | undefined, ms: number): { signal: AbortSignal; timedOut: () => boolean; done: () => void } {
  const timer = new AbortController();
  let expired = false;
  const id = setTimeout(() => { expired = true; timer.abort(new DOMException('timeout', 'TimeoutError')); }, ms);
  if (!caller) return { signal: timer.signal, timedOut: () => expired, done: () => clearTimeout(id) };
  if (typeof AbortSignal.any === 'function') {
    return { signal: AbortSignal.any([caller, timer.signal]), timedOut: () => expired && !caller.aborted, done: () => clearTimeout(id) };
  }
  // Запасной путь (старые WebView без AbortSignal.any): отмена вызывающего пробрасывается в наш контроллер с его причиной.
  const forward = () => timer.abort(caller.reason);
  if (caller.aborted) forward(); else caller.addEventListener('abort', forward, { once: true });
  return {
    signal: timer.signal,
    timedOut: () => expired && !caller.aborted,
    done: () => { clearTimeout(id); caller.removeEventListener('abort', forward); },
  };
}

async function request<T>(method: string, path: string, body?: unknown, signal?: AbortSignal, extra?: Record<string, string>): Promise<T> {
  const limit = withTimeout(signal, REQUEST_TIMEOUT_MS);
  try {
    return await send<T>(method, path, body, limit.signal, extra);
  } catch (error) {
    // Таймаут (на соединении или на чтении тела) — тот же вид, что «сеть есть, ответа нет»; отмена вызывающим — как была.
    if (limit.timedOut() && !(error instanceof ApiError)) {
      report({ method, path, status: 0, code: null, sent: body });
      throw new ApiError('unreachable', 0, null);
    }
    throw error;
  } finally {
    limit.done();
  }
}

async function send<T>(method: string, path: string, body: unknown, signal: AbortSignal, extra?: Record<string, string>): Promise<T> {
  const url = API_BASE + withParams(path, { lang });
  // X-Platform отличает наш трафик в аналитике бэкенда (UserDailyActivity) от мобильных клиентов.
  const headers: Record<string, string> = {
    'X-Access-Token': ACCESS_TOKEN, 'X-Platform': 'telegram', 'X-Device-Id': DEVICE_ID, 'X-App-Version': APP_VERSION,
    Accept: 'application/json',
  };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  Object.assign(headers, extra);
  let response: Response;
  try {
    response = await fetch(url, { method, headers, signal, body: body === undefined ? undefined : JSON.stringify(body) });
  } catch (error) {
    if (signal.aborted) throw error;
    report({ method, path, status: 0, code: null, sent: body });
    throw new ApiError(isOffline() ? 'network' : 'unreachable', 0, null);
  }
  if (!response.ok) {
    let code: string | null = null;
    let retryAfter: number | null = Number(response.headers.get('Retry-After')) || null;
    let payload: Record<string, unknown> | null = null;
    try {
      payload = (await response.json()) as Record<string, unknown>;
      code = typeof payload.code === 'string' ? payload.code : null;
      retryAfter = typeof payload.retry_after_seconds === 'number' ? payload.retry_after_seconds : retryAfter;
    } catch { /* тело не JSON — остаётся только статус */ }
    report({ method, path, status: response.status, code, reason: payload?.reason, sent: body });
    throw new ApiError('http', response.status, code, retryAfter, payload);
  }
  if (response.status === 204) { report({ method, path, status: 204, code: null, sent: body }); return undefined as T; }
  const data = (await response.json()) as T;
  report({ method, path, status: response.status, code: null, body: data, sent: body });
  return data;
}

/** GET экрана: с Bearer, если сессия есть. */
export function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  return apiRequest<T>('GET', path, { signal, auth: 'optional' });
}

export function apiSend<T>(method: 'POST' | 'PUT' | 'DELETE', path: string, body?: unknown): Promise<T> {
  return apiRequest<T>(method, path, { body, auth: 'optional' });
}

const isOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false;

/** Токен приложения не принят (401 invalid_access_token): повторять бессмысленно — экран «Приложение недоступно». */
export function isAppTokenRejected(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401 && error.code === 'invalid_access_token';
}

/** Ретраить только сеть и 5xx/429; 4xx — это ответ, не сбой. */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) return false;
  if (!(error instanceof ApiError)) return false;
  return error.kind !== 'http' || error.status >= 500 || error.status === 429;
}

/** Пауза перед повтором: у 429 — не меньше Retry-After сервера; иначе 1 с, 2 с, 4 с… (не больше 30 с). */
export function retryDelay(failureCount: number, error: unknown): number {
  if (error instanceof ApiError && error.status === 429 && error.retryAfterSeconds) return error.retryAfterSeconds * 1000;
  return Math.min(30_000, 1000 * 2 ** Math.max(0, failureCount - 1));
}
